import { __assign, __spreadArray } from "tslib";
import * as React from 'react';
import { Key } from 'ts-key-enum';
export var ComboboxContext = React.createContext(null);
export function useComboboxContext() {
    var context = React.useContext(ComboboxContext);
    if (!context) {
        throw new Error('useComboboxContext must be used within ComboboxContextProvider.');
    }
    return context;
}
var defaultComboboxProps = {
    isSearchable: true,
    closeOnSelect: true,
    showSelectedOptions: true,
};
function defaultSearchFilter(option, searchString) {
    if (typeof option.label === 'string')
        return option.label.toLowerCase().includes(searchString.toLowerCase());
    return option.searchLabel.toLowerCase().includes(searchString.toLowerCase());
}
export function ComboboxContextProvider(props) {
    var comboboxProps = __assign(__assign({}, defaultComboboxProps), props);
    var options = comboboxProps.options, controlledValue = comboboxProps.value, children = comboboxProps.children, closeOnSelect = comboboxProps.closeOnSelect, components = comboboxProps.components, defaultValue = comboboxProps.defaultValue, inputValue = comboboxProps.inputValue, disabled = comboboxProps.disabled, isSearchable = comboboxProps.isSearchable, onBlur = comboboxProps.onBlur, onChangeProp = comboboxProps.onChange, onClick = comboboxProps.onClick, onFocus = comboboxProps.onFocus, onKeyDown = comboboxProps.onKeyDown, onSearch = comboboxProps.onSearch, providedSearchFilter = comboboxProps.searchFilter, strings = comboboxProps.strings, autoFocus = comboboxProps.autoFocus, showSelectedOptions = comboboxProps.showSelectedOptions, showOnlyFilteredResults = comboboxProps.showOnlyFilteredResults;
    var isMulti = comboboxProps.isMulti;
    var announceOptionSelected = strings.announceOptionSelected, announceOptionDeselected = strings.announceOptionDeselected, announceValueCleared = strings.announceValueCleared, announceSearchResults = strings.announceSearchResults;
    var containerRef = React.useRef(null);
    var inputRef = React.useRef(null);
    var searchInputRef = React.useRef(null);
    var valueRefs = React.useRef([]);
    var _a = React.useState(options || []), filteredOptions = _a[0], setFilteredOptions = _a[1];
    var _b = React.useState(false), listboxHasFocus = _b[0], setListboxHasFocus = _b[1];
    var _c = React.useState(), searchString = _c[0], setSearchString = _c[1];
    var _d = React.useState([]), value = _d[0], setValue = _d[1];
    var _e = React.useState(autoFocus !== null && autoFocus !== void 0 ? autoFocus : false), inputHasFocus = _e[0], setInputHasFocus = _e[1];
    var _f = React.useState(), menuIsOpen = _f[0], setMenuIsOpen = _f[1];
    var _g = React.useState(''), offScreenAnnouncement = _g[0], setOffScreenAnnouncement = _g[1];
    var _h = React.useState(false), hasFocusWithin = _h[0], setHasFocusWithin = _h[1];
    var searchFilter = providedSearchFilter !== null && providedSearchFilter !== void 0 ? providedSearchFilter : defaultSearchFilter;
    var prevSearchString = React.useRef(searchString);
    var isOptionSelected = React.useCallback(function (option) { return Boolean(value.find(function (_a) {
        var value = _a.value;
        return value === option.value;
    })); }, [value]);
    var formatValue = React.useCallback(function (value) { return (Array.isArray(value) ? value : [value]); }, []);
    var filterOptionOrGroup = React.useCallback(function (optionOrGroup, searchString) {
        if ('options' in optionOrGroup) {
            var groupOptions = optionOrGroup.options.filter(function (option) {
                return (showSelectedOptions || (!showSelectedOptions && !isOptionSelected(option))) &&
                    searchFilter(option, searchString);
            });
            return groupOptions.length ? __assign(__assign({}, optionOrGroup), { options: groupOptions }) : [];
        }
        return searchFilter(optionOrGroup, searchString) ? optionOrGroup : [];
    }, [searchFilter, showSelectedOptions, isOptionSelected]);
    var handleSearch = React.useCallback(function () {
        if (searchString === null || searchString === void 0 ? void 0 : searchString.length) {
            var filtered = options === null || options === void 0 ? void 0 : options.flatMap(function (optionOrGroup) {
                return filterOptionOrGroup(optionOrGroup, searchString);
            });
            prevSearchString.current = searchString;
            setFilteredOptions(filtered !== null && filtered !== void 0 ? filtered : []);
            if (filtered) {
                setOffScreenAnnouncement(announceSearchResults(filtered.length, searchString));
            }
        }
        else if (prevSearchString.current && prevSearchString.current !== searchString) {
            setFilteredOptions(options !== null && options !== void 0 ? options : []);
            setOffScreenAnnouncement('');
            prevSearchString.current = '';
        }
    }, [searchString, options, announceSearchResults, filterOptionOrGroup]);
    function clearSearchValue() {
        if (searchInputRef.current) {
            searchInputRef.current.value = '';
        }
        setSearchString('');
    }
    function focusInput() {
        var _a;
        setInputHasFocus(true);
        setListboxHasFocus(false);
        if (inputRef.current) {
            (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
    }
    function focusSearch() {
        var _a;
        setListboxHasFocus(false);
        (_a = searchInputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }
    function focusListbox() {
        setListboxHasFocus(true);
    }
    function handleOnChange(value, data) {
        var _a;
        if (onChangeProp) {
            /**
             * If the onChange prop exists, redefine with proper type casting
             * and provide corresponding value format.
             */
            if (isMulti) {
                var onChange = onChangeProp;
                onChange(value, data);
            }
            else {
                var onChange = onChangeProp;
                onChange((_a = value[0]) !== null && _a !== void 0 ? _a : null, data);
            }
        }
    }
    function selectOption(selectOption, preventMenuClose) {
        var newValue = isMulti ? __spreadArray(__spreadArray([], value, true), [selectOption], false) : [selectOption];
        setValue(newValue);
        setOffScreenAnnouncement(announceOptionSelected(selectOption));
        handleOnChange(newValue, { action: 'select-option', option: selectOption });
        if (isSearchable) {
            clearSearchValue();
        }
        if (closeOnSelect && !preventMenuClose) {
            setMenuIsOpen(false);
        }
    }
    function deselectOption(deselectOption, preventMenuClose) {
        var newValue = isMulti ? value.filter(function (value) { return value.value !== deselectOption.value; }) : [];
        setValue(newValue);
        setOffScreenAnnouncement(announceOptionDeselected(deselectOption));
        handleOnChange(newValue, { action: 'deselect-option', option: deselectOption });
        if (isSearchable) {
            clearSearchValue();
        }
        if (closeOnSelect && !preventMenuClose) {
            setMenuIsOpen(false);
        }
    }
    function clearValue() {
        var newValue = [];
        setValue(newValue);
        setOffScreenAnnouncement(announceValueCleared);
        focusInput();
        if (isSearchable) {
            clearSearchValue();
        }
        handleOnChange(newValue, { action: 'clear' });
    }
    function handleOnClick(event) {
        if (inputHasFocus) {
            setMenuIsOpen(function (isOpen) { return !isOpen; });
        }
        if (onClick) {
            onClick(event);
        }
    }
    function handleOnKeyDown(event) {
        switch (event.key) {
            case Key.ArrowDown:
            case Key.Enter:
            case ' ': // Spacebar
                if (!menuIsOpen) {
                    setMenuIsOpen(true);
                    if (isSearchable) {
                        focusSearch();
                    }
                    else {
                        setListboxHasFocus(true);
                    }
                }
                event.preventDefault(); // prevent page scroll
                break;
        }
        if (onKeyDown) {
            onKeyDown(event);
        }
    }
    function handleOnFocus(event) {
        if (!disabled) {
            setInputHasFocus(true);
        }
        if (onFocus) {
            onFocus(event);
        }
    }
    function handleOnBlur(event) {
        setInputHasFocus(false);
        if (onBlur) {
            onBlur(event);
        }
    }
    function handleOnSearch(searchValue) {
        if (isSearchable) {
            setSearchString(searchValue);
            if (!menuIsOpen) {
                setMenuIsOpen(true);
            }
        }
    }
    function handleSearchOnKeyDown(event) {
        switch (event.key) {
            case Key.ArrowDown:
                event.preventDefault(); // prevent page scroll
                setListboxHasFocus(true);
                break;
            case Key.Escape:
                setMenuIsOpen(false);
                clearSearchValue();
                focusInput();
                event.preventDefault();
                break;
            case Key.Tab:
                if (event.shiftKey) {
                    setMenuIsOpen(false);
                    focusInput();
                }
                break;
        }
    }
    var handleMenuToggle = React.useCallback(function () {
        if (menuIsOpen && !isSearchable) {
            // Focus menu item when not searchable
            focusListbox();
        }
        if (!menuIsOpen && hasFocusWithin && !inputHasFocus && !disabled) {
            focusInput();
        }
    }, [menuIsOpen, isSearchable, hasFocusWithin, inputHasFocus, disabled]);
    React.useEffect(function () {
        handleMenuToggle();
    }, 
    /* eslint-disable-next-line react-hooks/exhaustive-deps */ // handleMenuToggle left out of dependency array
    [menuIsOpen]);
    React.useEffect(function () {
        if (typeof searchString === 'string') {
            handleSearch();
            if (onSearch) {
                onSearch(searchString);
            }
        }
    }, [searchString, handleSearch, onSearch]);
    React.useEffect(function () {
        var _a;
        if (controlledValue || defaultValue) {
            setValue(formatValue((_a = controlledValue !== null && controlledValue !== void 0 ? controlledValue : defaultValue) !== null && _a !== void 0 ? _a : []));
        }
    }, [controlledValue, defaultValue, setValue, formatValue]);
    React.useEffect(function () {
        if (inputRef.current) {
            inputRef.current.value = inputValue || '';
        }
    }, [inputValue]);
    React.useEffect(function () {
        if (options && !searchString) {
            setFilteredOptions(showOnlyFilteredResults ? [] : options);
        }
    }, [options, searchString, showOnlyFilteredResults]);
    var context = {
        filteredOptions: filteredOptions,
        containerRef: containerRef,
        inputRef: inputRef,
        searchInputRef: searchInputRef,
        valueRefs: valueRefs,
        handleOnClick: handleOnClick,
        handleOnKeyDown: handleOnKeyDown,
        handleOnFocus: handleOnFocus,
        handleOnBlur: handleOnBlur,
        handleOnSearch: handleOnSearch,
        handleSearchOnKeyDown: handleSearchOnKeyDown,
        value: value,
        selectOption: selectOption,
        deselectOption: deselectOption,
        clearValue: clearValue,
        hasValue: Boolean(value.length),
        isOptionSelected: isOptionSelected,
        focusInput: focusInput,
        focusSearch: focusSearch,
        focusListbox: focusListbox,
        setHasFocusWithin: setHasFocusWithin,
        menuIsOpen: Boolean(menuIsOpen),
        setMenuIsOpen: setMenuIsOpen,
        inputHasFocus: inputHasFocus,
        listboxHasFocus: listboxHasFocus,
        setListboxHasFocus: setListboxHasFocus,
        searchString: searchString,
        components: components,
        offScreenAnnouncement: offScreenAnnouncement,
        comboboxProps: comboboxProps,
        hasFocusWithin: hasFocusWithin,
    };
    // eslint-disable-next-line react/jsx-no-constructed-context-values
    return React.createElement(ComboboxContext.Provider, { value: __assign({}, context) }, children);
}
export default ComboboxContextProvider;
//# sourceMappingURL=Combobox.context.js.map